import * as React from 'react'

import {
  AppointmentFormTypes,
  AppointmentUtils,
  Components,
  Containers,
} from '@grandvisionhq/appointments'
import { Button, Delayed } from '@grandvisionhq/common'
import { Details, Text } from '@grandvisionhq/elements'
import {
  ComposedForm,
  FormControl,
  FormTypes,
  PhoneNumberInput,
  TextArea,
  Util,
} from '@grandvisionhq/forms'
import { Types } from '@grandvisionhq/graphql'
import { useIntl } from '@grandvisionhq/state'

import { consents, INTL_DEFAULTS, optIns } from '../../../configuration'
const { ConsentsAndOptIns } = Components
const { SignInBox } = Containers

const BookAppointmentForm: AppointmentFormTypes.FormComponent = ({
  layout,
  loggedIn,
  submitting = false,
  formAlert = null,
  onChange,
  disableSubmit = false,
  beforeSubmitSlot = null,
  ...rest
}) => {
  const { getLabel } = useIntl()
  const [state, setState] = React.useState<FormTypes.ComposedFormState | undefined>()

  const onChangeHandler = React.useCallback(
    (state: FormTypes.ComposedFormState) => {
      setState(state)
      onChange?.(state)
    },
    [onChange]
  )

  const stateData = React.useMemo(
    () => (state ? Util.getValuesFromComposedState(state.formState) : undefined),
    [state]
  )

  const phoneRequired = stateData?.additionalDetails?.preferSms === 'true'

  return (
    <ComposedForm {...rest} className="book-appointment-form" onChange={onChangeHandler}>
      {!loggedIn && <SignInBox />}
      <ComposedForm.Part name="personalDetails">
        <FormControl
          {...FormControl.defaultProps}
          id="firstName"
          data-t="personal-details-first-name"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.personalDetails.firstName'
          )}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.personalDetails.firstName'
          )}
        />
        <FormControl
          {...FormControl.defaultProps}
          id="lastName"
          data-t="personal-details-last-name"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.personalDetails.lastName'
          )}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.personalDetails.lastName'
          )}
        />
      </ComposedForm.Part>
      <ComposedForm.Part name="contactDetails">
        <FormControl
          {...FormControl.defaultProps}
          id="email"
          type="email"
          data-t="contact-details-email"
          label={getLabel('appointments.component.bookAppointmentForm.label.contactDetails.email')}
          disabled={loggedIn}
          validations={[Util.validations.isEmailAddress]}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.contactDetails.email'
          )}
        />
        <PhoneNumberInput
          {...FormControl.defaultProps}
          id="mobilePhone"
          data-t="contact-details-mobile-phone"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.contactDetails.mobilePhone'
          )}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.contactDetails.mobilePhone'
          )}
          required={phoneRequired}
          defaultCountry={INTL_DEFAULTS.country}
          localeForTranslations={INTL_DEFAULTS.locale}
          countriesOrder={['PT']}
        />
      </ComposedForm.Part>
      <ComposedForm.Part name="additionalDetails">
        <Details
          title={getLabel(
            'appointments.component.bookAppointmentForm.title.additionalDetails.addNote'
          )}
          subtitle={getLabel(
            'appointments.component.bookAppointmentForm.subtitle.additionalDetails.addNote'
          )}
        >
          <TextArea
            className="book-appointment-form__customerComment"
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.additionalDetails.customerComment'
            )}
            name="customerComment"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.additionalDetails.customerComment'
            )}
            maxLength={250}
            validations={[Util.validations.maxLength(250)]}
          />
        </Details>
      </ComposedForm.Part>
      {!loggedIn && (
        <ComposedForm.Part name="consentsAndOptIns">
          <ConsentsAndOptIns
            className="book-appointment-form__spacing-top"
            errorMessage={getLabel('appointments.component.consentsAndOptIns.error')}
            consents={consents}
            optIns={optIns}
          />
        </ComposedForm.Part>
      )}
      {formAlert}
      {beforeSubmitSlot}
      <Button
        type="submit"
        caption={getLabel('appointments.component.bookAppointmentForm.submit')}
        isLoading={submitting}
        disabled={disableSubmit}
        className="book-appointment-form__spacing-top"
      />

      {submitting && (
        <Delayed delay={2500} scrollIntoView className="book-appointment-form__spacing-top">
          <Text
            color="text-secondary"
            align="center"
            className="book-appointment-form__spacing-top"
          >
            {getLabel('appointments.component.bookAppointmentForm.slowLoadingIndication')}
          </Text>
        </Delayed>
      )}
    </ComposedForm>
  )
}

BookAppointmentForm.fromDataToState = (data) => {
  const personalDetails: Record<string, string | number> = {
    firstName: data.defaultBillingAddress?.firstName ?? data.personalDetails?.firstName ?? '',
    lastName: data.defaultBillingAddress?.lastName ?? data.personalDetails?.lastName ?? '',
  }
  const contactDetails: Record<string, string | number> = {
    email: data.email,
    mobilePhone: data.personalDetails?.phone ?? '',
  }

  const state: FormTypes.ComposedFormValueState = {
    personalDetails: Util.fieldsToFormState(personalDetails),
    contactDetails: Util.fieldsToFormState(contactDetails),
  }

  return state
}

BookAppointmentForm.getFormGroups = (): string[] => [
  'personalDetails',
  'contactDetails',
  'additionalDetails',
  'consentsAndOptIns',
]

BookAppointmentForm.fromStateToData = (state, { intl }, metadata) => {
  const data = Util.getValuesFromComposedState(state)
  const consentsAreDisplayed = data.consentsAndOptIns && 'createAccount' in data.consentsAndOptIns
  const optInsAreDisplayed = data.consentsAndOptIns && 'marketingEmails' in data.consentsAndOptIns
  const createAccountConsent = consents.find((consent) => consent.value === 'createAccount')
  const optIns = createAccountConsent?.children ?? []

  const result: AppointmentFormTypes.FormStateData = {
    personalDetails: {
      firstName: data.personalDetails?.firstName ?? '',
      lastName: data.personalDetails?.lastName ?? '',
      email: data.contactDetails?.email ?? '',
      phone: data.contactDetails?.mobilePhone ?? '',
      billingAddress: {
        firstName: data.personalDetails?.firstName ?? '',
        lastName: data.personalDetails?.lastName ?? '',
      },
    },
    optins:
      optInsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              id: 'marketingEmails',
              value: data.consentsAndOptIns?.marketingEmails === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('marketingEmails', optIns) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
            {
              id: 'marketingSMS',
              value: data.consentsAndOptIns?.marketingSMS === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('marketingSMS', optIns) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    customerComment: data.additionalDetails?.customerComment || null,
    preferredChannelOfCommunication: null,
    consents:
      consentsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              consentName: 'terms-conditions',
              value: data.consentsAndOptIns?.createAccount === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('createAccount', consents) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    child: null,
  }

  return result
}

export default BookAppointmentForm
